import styled from "@emotion/styled";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import { isLoading } from "../../Loading";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import Alert from "../Alert";
import useComplianceBannerProps from "../ComplianceBanner/hooks/useComplianceBannerProps";
import { AppButton } from "../Generic/AppButton";
import AppModal from "../Generic/AppModal/AppModal";
import { useMedflytAgencyId } from "../withMedflytAgencyId";

export type VisitRequestedSuccessModalProps = {
  type: "VisitRequestedSuccess";
  api: API;
  appState: AppState;
  onRequestNavigate: (page: Page) => void;
  onClickRequestMoreVisits: () => void;
};

interface Props {
  isOpen: boolean;
  modalProps: VisitRequestedSuccessModalProps;
}

export default function VisitRequestedSuccessModa(props: Props) {
  return (
    <AppModal
      type="success"
      isOpen={true}
      title={translate(STRINGS.SUCCESSFULY_REQUESTED)}
      message={`${translate(STRINGS.TO_SEE_THIS_VISIT_GO_TO)} ${translate(
        STRINGS.REQUESTED_LABEL_IN_CASES_LIST
      )}`}
      actions={[
        {
          key: "request-more-visits",
          variant: "soft",
          color: "primary",
          children: translate(STRINGS.REQUEST_MORE_VISITS),
          onClick: () => props.modalProps.onClickRequestMoreVisits(),
        },
      ]}
    >
      <ComplianceBanner {...props} />
    </AppModal>
  );
}

function ComplianceBanner(props: Props) {
  const medflytAgencyId = useMedflytAgencyId();
  const complianceBanner = useComplianceBannerProps({
    api: props.modalProps.api,
    appState: props.modalProps.appState,
    medflytAgencyId: isLoading(medflytAgencyId) ? null : medflytAgencyId,
    onComplianceBannerClick: () =>
      props.modalProps.onRequestNavigate({ type: "MyMedflytCompliance" }),
  });

  if (complianceBanner === null) {
    return null;
  }

  switch (complianceBanner.complianceStatus.type) {
    case "compliant":
      return null;

    case "notCompliantSoon":
      return (
        <AlertWrapper>
          <Alert
            type="danger"
            trailing={
              <AppButton variant="link" onClick={complianceBanner.onClick}>
                {translate(STRINGS.FIX_ISSUE)}
              </AppButton>
            }
          >
            {translate(STRINGS.YOU_WILL_FALL_OUT_OF_COMPLIANCE_IN_DAYS).replace(
              "%days%",
              complianceBanner.complianceStatus.inDays.toString()
            )}
          </Alert>
        </AlertWrapper>
      );

    case "notCompliant":
      return (
        <AlertWrapper>
          <Alert
            type="danger"
            trailing={
              <AppButton variant="link" onClick={complianceBanner.onClick}>
                {translate(STRINGS.FIX_ISSUE)}
              </AppButton>
            }
          >
            {translate(STRINGS.YOU_ARE_NOT_COMPLIANT_AND_IT_MAY_PREVENT_YOU_FROM)}
          </Alert>
        </AlertWrapper>
      );
  }
}

const AlertWrapper = styled.div`
  margin-top: 24px;
`;
